import React, { useState, useRef } from 'react';
import { importService } from '@/application/services';
import { HttpError } from '@/domain/entities';
import type { ImportStatusResponse } from '@/domain/entities';

interface ImportModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export const ImportModal: React.FC<ImportModalProps> = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<ImportStatusResponse | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Валидация
      const fileName = selectedFile.name.toLowerCase();
      const allowedExtensions = ['.xlsx', '.xls'];
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

      if (!hasValidExtension) {
        setError('Поддерживаются только Excel файлы (.xlsx, .xls)');
        setFile(null);
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10 MB
      if (selectedFile.size > maxSize) {
        setError('Размер файла не должен превышать 10 MB');
        setFile(null);
        return;
      }

      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const fileName = droppedFile.name.toLowerCase();
      const allowedExtensions = ['.xlsx', '.xls'];
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

      if (!hasValidExtension) {
        setError('Поддерживаются только Excel файлы (.xlsx, .xls)');
        return;
      }

      const maxSize = 10 * 1024 * 1024;
      if (droppedFile.size > maxSize) {
        setError('Размер файла не должен превышать 10 MB');
        return;
      }

      setFile(droppedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Пожалуйста, выберите файл');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const response = await importService.uploadFile(file);
      setJobId(response.job_id);
      setUploaded(true);
      
      // Начинаем опрос статуса
      startPolling(response.job_id);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message || 'Не удалось загрузить файл');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Не удалось загрузить файл');
      }
      setUploading(false);
    }
  };

  const startPolling = (id: string) => {
    // Очищаем предыдущий интервал
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Опрашиваем статус каждые 2 секунды
    const interval = setInterval(async () => {
      try {
        const statusResponse = await importService.getJobStatus(id);
        setStatus(statusResponse);

        // Если импорт завершен (успешно или с ошибками), останавливаем опрос
        if (
          statusResponse.status === 'completed' ||
          statusResponse.status === 'failed' ||
          statusResponse.status === 'cancelled'
        ) {
          clearInterval(interval);
          setPollingInterval(null);
          setUploading(false);
          
          if (statusResponse.status === 'completed' && onSuccess) {
            // Вызываем callback для обновления списка товаров
            setTimeout(() => {
              onSuccess();
            }, 1000);
          }
        }
      } catch (err) {
        console.error('Failed to get import status', err);
        // Продолжаем опрос даже при ошибке
      }
    }, 2000);

    setPollingInterval(interval);
  };

  const handleClose = () => {
    // Останавливаем опрос при закрытии
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    onClose();
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30';
      case 'failed':
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30';
      case 'processing':
        return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30';
      case 'pending':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'Завершен';
      case 'failed':
        return 'Завершен с ошибками';
      case 'processing':
        return 'Обрабатывается';
      case 'pending':
        return 'В очереди';
      case 'cancelled':
        return 'Отменен';
      default:
        return 'Неизвестен';
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Импорт товаров из Excel</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              disabled={uploading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!uploaded ? (
            <>
              {/* Инструкции */}
              <div className="mb-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200">Формат файла:</h3>
                  <a
                    href="https://storage.yandexcloud.net/iconlimits/primer.xlsx"
                    download="primer.xlsx"
                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium flex items-center gap-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Скачать пример
                  </a>
                </div>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                  <li>Файл должен быть в формате Excel (.xlsx или .xls)</li>
                  <li>Обязательные колонки: SKU, Категория, Размер, Цена</li>
                  <li>Опциональные колонки: Подкатегория, Пол, Год, Состояние, Скидка, Количество</li>
                  <li>Бренд, Название, Цвет и Фотографии загружаются автоматически по SKU</li>
                  <li>Максимальный размер файла: 10 MB</li>
                </ul>
              </div>

              {/* Drag & Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  error
                    ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30'
                    : file
                    ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30'
                    : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={uploading}
                />

                {!file ? (
                  <>
                    <svg
                      className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                      Перетащите файл сюда или{' '}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 font-medium"
                      >
                        выберите файл
                      </button>
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Поддерживаются только файлы .xlsx и .xls</p>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-16 h-16 mx-auto text-green-500 dark:text-green-400 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">{file.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      Удалить
                    </button>
                  </>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
                  {error}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Статус импорта */}
              {status && (
                <div className="space-y-4">
                  <div className={`rounded-lg p-4 ${getStatusColor(status.status)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Статус: {getStatusText(status.status)}</span>
                      {status.status === 'processing' && (
                        <svg
                          className="animate-spin h-5 w-5 text-blue-600 dark:text-blue-400"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      )}
                    </div>

                    {/* Progress */}
                    {status.status === 'processing' && status.total_rows > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span>
                            Обработано: {status.processed_rows} из {status.total_rows}
                          </span>
                          <span>
                            {status.progress !== undefined
                              ? `${status.progress.toFixed(1)}%`
                              : `${((status.processed_rows / status.total_rows) * 100).toFixed(1)}%`}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${
                                status.progress !== undefined
                                  ? status.progress
                                  : (status.processed_rows / status.total_rows) * 100
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Статистика */}
                    {(status.status === 'completed' || status.status === 'failed') && (
                      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Всего строк:</span>{' '}
                          <span className="font-semibold">{status.total_rows}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Успешно:</span>{' '}
                          <span className="font-semibold text-green-600 dark:text-green-400">{status.success_count}</span>
                        </div>
                        {status.failed_count > 0 && (
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Ошибок:</span>{' '}
                            <span className="font-semibold text-red-600 dark:text-red-400">{status.failed_count}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Ошибки */}
                    {status.errors && status.errors.length > 0 && (
                      <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <h4 className="font-semibold text-red-900 dark:text-red-200 mb-2">Ошибки импорта:</h4>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {status.errors.slice(0, 10).map((error, index) => (
                            <div key={index} className="text-sm text-red-800 dark:text-red-300">
                              {error.row && `Строка ${error.row}: `}
                              {error.sku && `SKU ${error.sku}: `}
                              {error.message}
                            </div>
                          ))}
                          {status.errors.length > 10 && (
                            <div className="text-sm text-red-600 dark:text-red-400 italic">
                              ... и ещё {status.errors.length - 10} ошибок
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Загрузка завершена, ожидание статуса */}
              {!status && (
                <div className="text-center py-8">
                  <svg
                    className="animate-spin h-8 w-8 mx-auto text-blue-600 dark:text-blue-400 mb-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <p className="text-gray-600 dark:text-gray-400">Файл загружен. Ожидание начала обработки...</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-gray-800">
          {!uploaded ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                disabled={uploading}
              >
                Отмена
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500 transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Загрузка...
                  </>
                ) : (
                  'Загрузить и импортировать'
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500 transition-colors"
              disabled={uploading && status?.status === 'processing'}
            >
              {status?.status === 'completed' || status?.status === 'failed' ? 'Закрыть' : 'Отмена'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

